import React, {useEffect, useState} from 'react'
import {Modal, Button, Form, FormControl, FormGroup, FormLabel, InputGroup} from 'react-bootstrap'
import {
  selectEditingBranchUser,
  selectShowEditBranchUserModal,
  setShowEditBranchUserModal,
  updateBranchUser,
} from '../redux/BranchRedux'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {useAppSelector} from '../../../../setup/redux/hooks'

const EditUserBranchModal: React.FC = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const visible = useAppSelector(selectShowEditBranchUserModal)
  const editingUser = useAppSelector(selectEditingBranchUser)

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(1, intl.formatMessage({id: 'BRANCH.FIRST_NAME_MIN'}))
      .max(30, intl.formatMessage({id: 'BRANCH.FIRST_NAME_MAX'}))
      .required(intl.formatMessage({id: 'BRANCH.FIRST_NAME_REQUIRED'})),

    lastName: Yup.string()
      .min(1, intl.formatMessage({id: 'BRANCH.LAST_NAME_MIN'}))
      .max(30, intl.formatMessage({id: 'BRANCH.LAST_NAME_MAX'}))
      .required(intl.formatMessage({id: 'BRANCH.LAST_NAME_REQUIRED'})),

    password: Yup.string()
      .min(8, intl.formatMessage({id: 'BRANCH.PASSWORD_MIN'}))
      .required(intl.formatMessage({id: 'BRANCH.PASSWORD_REQUIRED'})),

    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], intl.formatMessage({id: 'BRANCH.PASSWORD_MISMATCH'}))
      .required(intl.formatMessage({id: 'BRANCH.CONFIRM_PASSWORD_REQUIRED'})),
  })

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: (values) => {
      if (editingUser) {
        dispatch(
          updateBranchUser({
            userId: editingUser.id,
            firstName: values.firstName,
            lastName: values.lastName,
            password: values.password,
          })
        )
      }
    },
  })

  useEffect(() => {
    if (visible && editingUser) {
      formik.resetForm({
        values: {
          firstName: editingUser.firstName,
          lastName: editingUser.lastName,
          email: editingUser.email,
          password: '',
          confirmPassword: '',
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, editingUser])

  const handleClose = () => {
    dispatch(setShowEditBranchUserModal({visible: false, user: null}))
  }

  return (
    <Modal show={visible} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage({id: 'BRANCH.EDIT_USER'})}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <FormGroup className='mb-3'>
            <FormLabel>{intl.formatMessage({id: 'BRANCH.FIRST_NAME'})}</FormLabel>
            <FormControl
              {...formik.getFieldProps('firstName')}
              isInvalid={formik.touched.firstName && !!formik.errors.firstName}
            />
            <Form.Control.Feedback type='invalid'>{formik.errors.firstName}</Form.Control.Feedback>
          </FormGroup>

          <FormGroup className='mb-3'>
            <FormLabel>{intl.formatMessage({id: 'BRANCH.LAST_NAME'})}</FormLabel>
            <FormControl
              {...formik.getFieldProps('lastName')}
              isInvalid={formik.touched.lastName && !!formik.errors.lastName}
            />
            <Form.Control.Feedback type='invalid'>{formik.errors.lastName}</Form.Control.Feedback>
          </FormGroup>

          <FormGroup className='mb-3'>
            <FormLabel>{intl.formatMessage({id: 'BRANCH.EMAIL'})}</FormLabel>
            <FormControl
              type='email'
              {...formik.getFieldProps('email')}
              disabled
              style={{backgroundColor: '#e9ecef'}}
            />
          </FormGroup>

          <FormGroup className='mb-3'>
            <FormLabel>{intl.formatMessage({id: 'BRANCH.PASSWORD'})}</FormLabel>
            <InputGroup>
              <FormControl
                type={showPassword ? 'text' : 'password'}
                {...formik.getFieldProps('password')}
                isInvalid={formik.touched.password && !!formik.errors.password}
              />
              <Button variant='outline-secondary' onClick={() => setShowPassword(!showPassword)}>
                <i className={`bi ${showPassword ? 'bi-eye' : 'bi-eye-slash'}`}></i>
              </Button>
              <Form.Control.Feedback type='invalid'>{formik.errors.password}</Form.Control.Feedback>
            </InputGroup>
          </FormGroup>

          <FormGroup className='mb-3'>
            <FormLabel>{intl.formatMessage({id: 'BRANCH.CONFIRM_PASSWORD'})}</FormLabel>
            <InputGroup>
              <FormControl
                type={showConfirmPassword ? 'text' : 'password'}
                {...formik.getFieldProps('confirmPassword')}
                isInvalid={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
              />
              <Button
                variant='outline-secondary'
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                <i className={`bi ${showConfirmPassword ? 'bi-eye' : 'bi-eye-slash'}`}></i>
              </Button>
              <Form.Control.Feedback type='invalid'>
                {formik.errors.confirmPassword}
              </Form.Control.Feedback>
            </InputGroup>
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleClose}>
            {intl.formatMessage({id: 'SYSTEM.CANCEL'})}
          </Button>
          <Button variant='primary' type='submit'>
            {intl.formatMessage({id: 'SYSTEM.SAVE'})}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default EditUserBranchModal
